/**
 * Selection state machine.
 *
 *   idle ── start(mode) ───────────▶ selecting
 *   selecting ── Esc ──────────────▶ idle
 *   selecting ── commit gesture ──▶ composing
 *   composing ── confirm / save ──▶ selecting (cleared)
 *   composing ── Esc ──────────────▶ selecting (cleared)
 *
 * Window-level capture listeners route real DOM events into the
 * appropriate phase transitions. UI components observe state via
 * `subscribe(listener)`.
 */
import { rectFromPoints, isRectNonTrivial } from "./dragRect.js";
import { TypedEmitter } from "./events.js";
import { freezeCanvas } from "./freeze.js";
import { hitTest, isOwnEvent } from "./hitTest.js";
import { KEY_CANCEL, KEY_COMMIT, matchesBinding } from "./keyboard.js";
const idleState = (mode) => ({
    phase: "idle",
    mode,
    frozen: false,
    paused: false,
    markersHidden: false,
    pickingSecondary: false,
    pointer: null,
    hovered: null,
    selected: [],
    dragRect: null,
    textRange: null,
});
export class SelectionEngine {
    emitter = new TypedEmitter();
    listeners = new Set();
    state;
    dragOrigin = null;
    isDragging = false;
    unfreeze = null;
    ignoreRoots;
    freezeOptions;
    constructor(options) {
        this.ignoreRoots = options.ignoreRoots;
        this.freezeOptions = options.freeze ?? {};
        this.state = idleState(options.initialMode ?? "element");
    }
    /* ------------------------------------------------------------ */
    /* Public API                                                    */
    /* ------------------------------------------------------------ */
    getState() {
        return this.state;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    on(event, handler) {
        return this.emitter.on(event, handler);
    }
    start(mode) {
        if (this.state.phase !== "idle")
            return;
        const nextMode = mode ?? this.state.mode;
        this.attachListeners();
        this.update({
            ...this.state,
            phase: "selecting",
            mode: nextMode,
            pointer: null,
            hovered: null,
            selected: [],
            dragRect: null,
            textRange: null,
        });
    }
    stop() {
        if (this.state.phase === "idle")
            return;
        this.detachListeners();
        this.releaseFreeze();
        this.dragOrigin = null;
        this.isDragging = false;
        this.update(idleState(this.state.mode));
        this.emitter.emit("cancel", { reason: "stop" });
    }
    setMode(mode) {
        if (this.state.mode === mode)
            return;
        this.dragOrigin = null;
        this.isDragging = false;
        this.update({
            ...this.state,
            mode,
            hovered: null,
            selected: [],
            dragRect: null,
            textRange: null,
        });
    }
    togglePaused() {
        this.update({ ...this.state, paused: !this.state.paused });
    }
    toggleMarkersHidden() {
        this.update({ ...this.state, markersHidden: !this.state.markersHidden });
    }
    setFrozen(frozen) {
        if (frozen === this.state.frozen)
            return;
        if (frozen) {
            if (!this.unfreeze && this.freezeOptions !== false) {
                this.unfreeze = freezeCanvas(this.freezeOptions);
            }
        }
        else {
            this.releaseFreeze();
        }
        this.update({ ...this.state, frozen });
    }
    cancel(reason = "user") {
        if (this.state.phase === "composing") {
            this.update({ ...this.state, phase: "selecting" });
            this.emitter.emit("compose-cancel", undefined);
            return;
        }
        if (this.state.phase === "selecting") {
            this.update({
                ...this.state,
                hovered: null,
                selected: [],
                dragRect: null,
                textRange: null,
            });
            this.emitter.emit("cancel", { reason });
        }
    }
    commit() {
        if (this.state.phase !== "selecting")
            return;
        const commit = this.buildCommit();
        if (!commit)
            return;
        this.update({ ...this.state, phase: "composing" });
        this.emitter.emit("commit", commit);
        this.emitter.emit("compose-open", commit);
    }
    finishCompose() {
        if (this.state.phase !== "composing")
            return;
        this.update({
            ...this.state,
            phase: "selecting",
            hovered: null,
            selected: [],
            dragRect: null,
            textRange: null,
        });
    }
    /**
     * Enter one-shot "pick another element" mode. The next pointerdown
     * (anywhere outside ignore-roots) is captured, the picked element is
     * emitted via `secondary-picked`, and the flag self-resets.
     *
     * Used by the rearrange flow to ask the user for a destination after
     * the source has already been chosen.
     */
    startSecondaryPick() {
        this.update({ ...this.state, pickingSecondary: true });
    }
    cancelSecondaryPick() {
        if (!this.state.pickingSecondary)
            return;
        this.update({ ...this.state, pickingSecondary: false });
        this.emitter.emit("secondary-cancelled", undefined);
    }
    /* ------------------------------------------------------------ */
    /* Listener wiring                                              */
    /* ------------------------------------------------------------ */
    attachListeners() {
        window.addEventListener("pointermove", this.onPointerMove, {
            capture: true,
            passive: true,
        });
        window.addEventListener("pointerdown", this.onPointerDown, {
            capture: true,
        });
        window.addEventListener("pointerup", this.onPointerUp, { capture: true });
        window.addEventListener("keydown", this.onKeyDown, { capture: true });
        document.addEventListener("selectionchange", this.onSelectionChange);
    }
    detachListeners() {
        window.removeEventListener("pointermove", this.onPointerMove, {
            capture: true,
        });
        window.removeEventListener("pointerdown", this.onPointerDown, {
            capture: true,
        });
        window.removeEventListener("pointerup", this.onPointerUp, {
            capture: true,
        });
        window.removeEventListener("keydown", this.onKeyDown, {
            capture: true,
        });
        document.removeEventListener("selectionchange", this.onSelectionChange);
    }
    releaseFreeze() {
        if (this.unfreeze) {
            this.unfreeze();
            this.unfreeze = null;
        }
    }
    /* ------------------------------------------------------------ */
    /* Event handlers                                                */
    /* ------------------------------------------------------------ */
    onPointerMove = (event) => {
        const pointer = { x: event.clientX, y: event.clientY };
        if (this.state.pickingSecondary) {
            const hovered = hitTest(event.clientX, event.clientY, this.ignoreRoots);
            if (hovered !== this.state.hovered ||
                pointer.x !== this.state.pointer?.x ||
                pointer.y !== this.state.pointer?.y) {
                this.update({ ...this.state, pointer, hovered });
            }
            return;
        }
        if (this.state.phase !== "selecting" || this.state.paused)
            return;
        if (this.state.mode === "drag-area" && this.isDragging && this.dragOrigin) {
            this.update({
                ...this.state,
                pointer,
                dragRect: rectFromPoints(this.dragOrigin, pointer),
            });
            return;
        }
        if (this.state.mode === "text") {
            this.update({ ...this.state, pointer });
            return;
        }
        const hovered = hitTest(event.clientX, event.clientY, this.ignoreRoots);
        if (hovered === this.state.hovered &&
            pointer.x === this.state.pointer?.x &&
            pointer.y === this.state.pointer?.y) {
            return;
        }
        this.update({ ...this.state, pointer, hovered });
    };
    onPointerDown = (event) => {
        if (isOwnEvent(event, this.ignoreRoots))
            return;
        if (event.button !== 0)
            return;
        // pickingSecondary takes priority — captures next click for rearrange
        // destination, irrespective of phase/mode.
        if (this.state.pickingSecondary) {
            event.preventDefault();
            event.stopImmediatePropagation();
            const target = hitTest(event.clientX, event.clientY, this.ignoreRoots);
            if (target) {
                this.update({ ...this.state, pickingSecondary: false });
                this.emitter.emit("secondary-picked", { element: target });
            }
            else {
                this.update({ ...this.state, pickingSecondary: false });
                this.emitter.emit("secondary-cancelled", undefined);
            }
            return;
        }
        if (this.state.phase !== "selecting" || this.state.paused)
            return;
        if (this.state.mode === "text")
            return; // let native text selection happen
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.state.mode === "drag-area") {
            this.dragOrigin = { x: event.clientX, y: event.clientY };
            this.isDragging = true;
            this.update({
                ...this.state,
                dragRect: {
                    x: event.clientX,
                    y: event.clientY,
                    width: 0,
                    height: 0,
                },
            });
            return;
        }
        const target = hitTest(event.clientX, event.clientY, this.ignoreRoots);
        if (!target)
            return;
        if (this.state.mode === "element") {
            this.update({ ...this.state, selected: [target] });
            this.commit();
            return;
        }
        if (this.state.mode === "multi-select") {
            const existing = this.state.selected.indexOf(target);
            const next = existing >= 0
                ? this.state.selected.filter((_, i) => i !== existing)
                : [...this.state.selected, target];
            this.update({ ...this.state, selected: next });
        }
    };
    onPointerUp = (event) => {
        if (this.state.phase !== "selecting" || this.state.paused)
            return;
        if (this.state.mode !== "drag-area")
            return;
        if (!this.isDragging || !this.dragOrigin)
            return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const rect = rectFromPoints(this.dragOrigin, {
            x: event.clientX,
            y: event.clientY,
        });
        this.isDragging = false;
        this.dragOrigin = null;
        if (!isRectNonTrivial(rect)) {
            this.update({ ...this.state, dragRect: null });
            return;
        }
        this.update({ ...this.state, dragRect: rect });
        this.commit();
    };
    onKeyDown = (event) => {
        if (this.state.phase === "idle")
            return;
        if (matchesBinding(event, KEY_CANCEL)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.cancel("user");
            return;
        }
        if (this.state.phase !== "selecting")
            return;
        if (matchesBinding(event, KEY_COMMIT)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.commit();
        }
    };
    onSelectionChange = () => {
        if (this.state.phase !== "selecting" || this.state.mode !== "text")
            return;
        const sel = document.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
            if (this.state.textRange) {
                this.update({ ...this.state, textRange: null });
            }
            return;
        }
        const range = sel.getRangeAt(0);
        this.update({ ...this.state, textRange: range.cloneRange() });
    };
    /* ------------------------------------------------------------ */
    /* Internals                                                     */
    /* ------------------------------------------------------------ */
    buildCommit() {
        const { mode, selected, dragRect, textRange, pointer } = this.state;
        const anchor = pointer ?? { x: 0, y: 0 };
        if (mode === "element") {
            const primary = selected[0];
            if (!primary)
                return null;
            return { mode, primary, additional: [], dragRect: null, textRange: null, anchor };
        }
        if (mode === "multi-select") {
            const primary = selected[0];
            if (!primary)
                return null;
            return {
                mode,
                primary,
                additional: selected.slice(1),
                dragRect: null,
                textRange: null,
                anchor,
            };
        }
        if (mode === "text") {
            if (!textRange)
                return null;
            const node = textRange.commonAncestorContainer;
            const container = node.nodeType === Node.ELEMENT_NODE
                ? node
                : node.parentElement;
            if (!container)
                return null;
            const r = textRange.getBoundingClientRect();
            return {
                mode,
                primary: container,
                additional: [],
                dragRect: null,
                textRange,
                anchor: { x: r.right, y: r.bottom },
            };
        }
        if (mode === "drag-area") {
            if (!dragRect)
                return null;
            const cx = dragRect.x + dragRect.width / 2;
            const cy = dragRect.y + dragRect.height / 2;
            const anchorEl = hitTest(cx, cy, this.ignoreRoots) ?? document.documentElement;
            return {
                mode,
                primary: anchorEl,
                additional: [],
                dragRect,
                textRange: null,
                anchor: {
                    x: dragRect.x + dragRect.width,
                    y: dragRect.y + dragRect.height,
                },
            };
        }
        return null;
    }
    update(next) {
        this.state = next;
        for (const listener of Array.from(this.listeners)) {
            listener(next);
        }
    }
}
//# sourceMappingURL=SelectionEngine.js.map